import {useMemo, useState} from "react";
import type {PersonModelInterface} from "@/models/personModel";

import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Separator} from "@/components/ui/separator";
import {Button} from "@/components/ui/button";

import {
    Smartphone,
    Hash,
    Cloud,
    KeyRound,
    ShieldCheck,
    FileBadge,
    Eye,
    EyeOff,
} from "lucide-react";

import PhoneInfoForms from "@/components/Forms/PhoneInfo/PhoneInfoForms";

function Row({
                 icon: Icon,
                 label,
                 value,
                 right,
             }: {
    icon: any;
    label: string;
    value?: React.ReactNode;
    right?: React.ReactNode;
}) {
    const empty = value === undefined || value === null || value === "" || value === "—";
    return (
        <div className="flex items-start gap-3 py-2">
            <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                <Icon className="h-4 w-4 text-muted-foreground"/>
            </div>

            <div className="min-w-0 flex-1">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className={`text-sm break-words ${empty ? "text-muted-foreground" : ""}`}>
                    {empty ? "—" : value}
                </div>
            </div>

            {right ? <div className="pt-0.5">{right}</div> : null}
        </div>
    );
}

function maskImei(v?: string) {
    if (!v) return "";
    const s = String(v).trim();
    if (s.length <= 6) return s;
    const start = s.slice(0, 3);
    const end = s.slice(-3);
    return `${start}${"•".repeat(Math.max(0, s.length - 6))}${end}`;
}

function maskPassword(v?: string) {
    if (!v) return "";
    return "•".repeat(Math.min(10, Math.max(6, v.length)));
}

export function PhoneInfoCard({
                                  person,
                                  isEditing,
                              }: {
    person: PersonModelInterface;
    isEditing: boolean;
}) {
    const [showSecrets, setShowSecrets] = useState(false);

    const phone: any = (person as any).phone || {};
    const phoneModel = phone.phoneModel || "";
    const imei = phone.imei || "";
    const icloud = phone.icloud || "";
    const password = phone.password || "";
    const condition = phone.condition || "";
    const registrationNumber = phone.registrationNumber || "";

    const hasAny =
        phoneModel || imei || icloud || password || condition || registrationNumber;

    const imeiView = useMemo(() => (showSecrets ? imei : maskImei(imei)), [showSecrets, imei]);
    const passView = useMemo(
        () => (showSecrets ? password : maskPassword(password)),
        [showSecrets, password]
    );

    if (isEditing) {
        return (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Smartphone className="h-4 w-4"/>
                        Информация о телефоне
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <PhoneInfoForms/>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Smartphone className="h-4 w-4"/>
                        Информация о телефоне
                    </CardTitle>

                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSecrets((v) => !v)}
                        className="h-8"
                    >
                        {showSecrets ? <EyeOff className="h-4 w-4 mr-2"/> : <Eye className="h-4 w-4 mr-2"/>}
                        {showSecrets ? "Скрыть" : "Показать"}
                    </Button>
                </div>
            </CardHeader>

            <CardContent className="space-y-3">
                {!hasAny ? (
                    <div className="text-sm text-muted-foreground">—</div>
                ) : (
                    <>
                        <div className="rounded-lg border bg-muted/20 p-3">
                            <div className="text-xs text-muted-foreground">Модель</div>
                            <div className="text-base font-semibold leading-tight">
                                {phoneModel || "—"}
                            </div>
                        </div>

                        <div className="grid gap-1 md:grid-cols-2">
                            <Row icon={Hash} label="IMEI" value={imei ? imeiView : "—"}/>
                            <Row icon={Cloud} label="iCloud" value={icloud || "—"}/>
                            <Row icon={KeyRound} label="Пароль" value={password ? passView : "—"}/>
                            <Row icon={ShieldCheck} label="Состояние телефона" value={condition || "—"}/>
                            <Row icon={FileBadge} label="Номер регистрации" value={registrationNumber || "—"}/>
                        </div>

                        <Separator/>
                        <div className="text-xs text-muted-foreground">
                            В просмотре IMEI и пароль скрыты по умолчанию
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
